import { O, pipe, RA, RR, RNEA } from '@packages/prelude'
import * as domain from '@packages/domain'
import { VisibleSteps, ConfigurationHelpers } from '~/utils'
import { Configuration } from '~/store'

export const totalPrice = (state: Configuration.State, basePrice: number): number => {
  const prunedState = ConfigurationHelpers.pruneConfigurationState(state)

  return pipe(
    prunedState.selectedOptions, // Uses only valid, reachable options
    RR.values,
    RA.flatten,
    RA.map(option => option.price),
    RA.reduce(basePrice, (sum, price) => sum + price)
  )
}

/**
 * Get the first step ID from all steps
 */
export const getFirstStepId = (state: Configuration.State): O.Option<domain.StepId.Type> => {
  return pipe(
    state.allSteps,
    RA.head,
    O.map(step => step.id)
  )
}

/**
 * Get the next sequential (non-optional) step after current step using order field
 */
export const getNextSequentialStep = (
  currentStepId: domain.StepId.Type,
  state: Configuration.State
): O.Option<domain.StepId.Type> => {
  const visible = VisibleSteps.visibleSteps(state)

  return pipe(
    visible,
    RA.findFirst(step => domain.StepId.Eq.equals(step.id, currentStepId)),
    O.chain(currentStep =>
      pipe(
        visible,
        RA.filter(step => step.order > currentStep.order && step.optional === false),
        RA.head, // visibleSteps is already sorted by order
        O.map(step => step.id)
      )
    )
  )
}

/**
 * Get editable steps - steps that can be configured
 * Derived from visibleSteps filtered by enabledSteps
 */
export const editableSteps = (state: Configuration.State): ReadonlyArray<domain.Step.Type> => {
  const visible = VisibleSteps.visibleSteps(state)

  return pipe(
    visible,
    RA.filter(step => pipe(state.enabledSteps, RA.elem(domain.StepId.Eq)(step.id)))
  )
}

/**
 * Get preview steps - steps that are visible but not editable
 * Derived from visibleSteps filtered by what's NOT in enabledSteps
 */
export const previewSteps = (state: Configuration.State): ReadonlyArray<domain.Step.Type> => {
  const visible = VisibleSteps.visibleSteps(state)

  return pipe(
    visible,
    RA.filter(step => pipe(state.enabledSteps, RA.elem(domain.StepId.Eq)(step.id), enabled => !enabled))
  )
}

export const isStepConfigured = (state: Configuration.State, stepId: domain.StepId.Type): boolean => {
  return pipe(state.selectedOptions, RR.lookup(String(stepId)), O.chain(RNEA.fromReadonlyArray), O.isSome)
}

// -----------------------------------------------------------------------------
// Navigation State Selectors
// -----------------------------------------------------------------------------

export const canNavigateNext = (state: Configuration.State): boolean => {
  return pipe(
    state.currentStepId,
    O.chain(currentStepId => pipe(state.selectedOptions, RR.lookup(String(currentStepId)))),
    O.chain(RNEA.fromReadonlyArray),
    O.isSome
  )
}

/**
 * Check if previous navigation is possible
 */
export const canNavigatePrevious = (state: Configuration.State): boolean => {
  return pipe(
    O.Do,
    O.apS('firstStepId', getFirstStepId(state)),
    O.apS('currentStepId', state.currentStepId),
    O.map(({ currentStepId, firstStepId }) => !domain.StepId.Eq.equals(currentStepId, firstStepId)),
    O.getOrElse(() => false)
  )
}

// -----------------------------------------------------------------------------
// Progress Selectors
// -----------------------------------------------------------------------------

/**
 * Get completion progress (number of configured steps)
 */
export const getProgress = (state: Configuration.State): number => {
  const editable = editableSteps(state)

  return pipe(
    editable,
    RA.filterMap(step => pipe(state.selectedOptions, RR.lookup(String(step.id)), O.chain(RNEA.fromReadonlyArray))),
    RA.size
  )
}

/**
 * Get total progress count - number of visible steps
 */
export const getProgressTotal = (state: Configuration.State): number => {
  return pipe(VisibleSteps.visibleSteps(state), RA.size)
}

/**
 * Check if the configuration is complete (i.e. all editable steps have been configured)
 */
export const isConfigurationComplete = (state: Configuration.State): boolean => {
  return pipe(
    state,
    editableSteps,
    RNEA.fromReadonlyArray,
    O.map(RA.every(step => pipe(state.selectedOptions, RR.lookup(String(step.id)), O.isSome))),
    O.getOrElse(() => false)
  )
}

// -----------------------------------------------------------------------------
// Step State Selectors
// -----------------------------------------------------------------------------

export const getNextAdjacentStep = (
  state: Configuration.State,
  currentStepId: domain.StepId.Type
): O.Option<domain.Step.Type> => {
  return pipe(
    state,
    editableSteps,
    RA.findIndex(step => domain.StepId.Eq.equals(step.id, currentStepId)),
    O.chain(index => pipe(state, editableSteps, RA.lookup(index + 1)))
  )
}

export const getUncollapseStepIds = (state: Configuration.State): ReadonlyArray<domain.StepId.Type> => {
  return pipe(
    state.currentStepId,
    O.map(currentStepId =>
      pipe(
        getNextAdjacentStep(state, currentStepId),
        O.filter(step => !isStepConfigured(state, step.id)),
        O.map(step => [currentStepId, step.id]),
        O.getOrElse(() => [currentStepId])
      )
    ),
    O.getOrElse(() => [] as ReadonlyArray<domain.StepId.Type>)
  )
}
